/**
 * Early Supporter API client (/api/early-supporter).
 * All responses are wrapped as { success, data } like the rest of the API.
 */

import apiClient from './api';
import type {
  EsCheckoutRedirect,
  EsCheckoutStatusView,
  EsCreateCheckoutRequest,
  EsEntitlementView,
  EsOffer,
  EsQuote,
  EsQuoteRequest,
  EsSwitchToYearlyRequest,
  EsUpgradeQuote,
  EsUpgradeRequest,
} from '@/types/earlySupporter';

const BASE = '/api/early-supporter';

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const earlySupporterApi = {
  async getOffer(): Promise<EsOffer> {
    const res = await apiClient.get<Envelope<EsOffer>>(`${BASE}/offer`);
    return res.data.data;
  },

  async getEntitlement(): Promise<EsEntitlementView | null> {
    const res = await apiClient.get<Envelope<{ entitlement: EsEntitlementView | null }>>(`${BASE}/entitlement`);
    return res.data.data.entitlement;
  },

  async quote(body: EsQuoteRequest): Promise<EsQuote> {
    const res = await apiClient.post<Envelope<EsQuote>>(`${BASE}/quote`, body);
    return res.data.data;
  },

  async createCheckout(body: EsCreateCheckoutRequest): Promise<EsCheckoutRedirect> {
    const res = await apiClient.post<Envelope<EsCheckoutRedirect>>(`${BASE}/checkouts`, body, { timeout: 45_000 });
    return res.data.data;
  },

  async getCheckout(id: string): Promise<EsCheckoutStatusView> {
    const res = await apiClient.get<Envelope<EsCheckoutStatusView>>(`${BASE}/checkouts/${encodeURIComponent(id)}`);
    return res.data.data;
  },

  async cancel(): Promise<EsEntitlementView> {
    const res = await apiClient.post<Envelope<EsEntitlementView>>(`${BASE}/entitlement/cancel`);
    return res.data.data;
  },

  async resume(): Promise<EsEntitlementView> {
    const res = await apiClient.post<Envelope<EsEntitlementView>>(`${BASE}/entitlement/resume`);
    return res.data.data;
  },

  async switchToYearly(body: EsSwitchToYearlyRequest): Promise<EsCheckoutRedirect> {
    const res = await apiClient.post<Envelope<EsCheckoutRedirect>>(`${BASE}/entitlement/switch-to-yearly`, body, {
      timeout: 45_000,
    });
    return res.data.data;
  },

  async quoteUpgrade(body: EsUpgradeRequest): Promise<EsUpgradeQuote> {
    const res = await apiClient.post<Envelope<EsUpgradeQuote>>(`${BASE}/entitlement/upgrade-quote`, body, {
      timeout: 30_000,
    });
    return res.data.data;
  },

  async upgrade(body: EsUpgradeRequest): Promise<EsCheckoutRedirect> {
    const res = await apiClient.post<Envelope<EsCheckoutRedirect>>(`${BASE}/entitlement/upgrade`, body, {
      timeout: 45_000,
    });
    return res.data.data;
  },
};
